import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { DEFAULT_SETTINGS, type Settings } from '@clawhire/shared';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SkeletonLines } from '@/components/ui/skeleton';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { AdminNote, SettingsCard, SettingsSection } from '@/pages/settings/SettingsSection';
import { TagEditor } from '@/pages/settings/TagEditor';
import { useSettingsQuery, useUpdateSettings } from '@/pages/settings/useSettings';

/** Number field that accepts what an `<input type="number">` yields (string) and validates the result. */
function numberField(opts: { int?: boolean; min?: number; max?: number; positive?: boolean }) {
  return z.union([z.number(), z.string()]).transform((v, ctx) => {
    const raw = typeof v === 'string' ? v.trim().replace(',', '.') : v;
    if (raw === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required' });
      return z.NEVER;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a number' });
      return z.NEVER;
    }
    if (opts.int && !Number.isInteger(n)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a whole number' });
      return z.NEVER;
    }
    if (opts.positive && n <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Must be greater than 0' });
      return z.NEVER;
    }
    if (opts.min !== undefined && n < opts.min) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `At least ${opts.min}` });
      return z.NEVER;
    }
    if (opts.max !== undefined && n > opts.max) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `At most ${opts.max}` });
      return z.NEVER;
    }
    return n;
  });
}

const schema = z.object({
  margin_pct: numberField({ min: 0, max: 99.99 }),
  hours_per_day: numberField({ positive: true, max: 24 }),
  feedback_due_working_days: numberField({ int: true, min: 0 }),
  urgent_start_days: numberField({ int: true, min: 0 }),
  consent_retention_months: numberField({ int: true, min: 1 }),
  forbidden_terms: z.array(z.string().trim().min(1)),
});
type FormInput = z.input<typeof schema>;
type FormOutput = z.output<typeof schema>;

function toValues(s: Settings): FormInput {
  return {
    margin_pct: s.margin_pct,
    hours_per_day: s.hours_per_day,
    feedback_due_working_days: s.feedback_due_working_days,
    urgent_start_days: s.urgent_start_days,
    consent_retention_months: s.consent_retention_months,
    forbidden_terms: [...s.forbidden_terms],
  };
}

/** General settings (spec §3): margin, hours per day, deadlines, retention, forbidden terms. */
export function GeneralSettings() {
  const { isAdmin } = useAuth();
  const settings = useSettingsQuery();
  const update = useUpdateSettings();

  const values = React.useMemo(() => (settings.data ? toValues(settings.data) : undefined), [settings.data]);
  const form = useForm<FormInput, unknown, FormOutput>({
    resolver: zodResolver(schema),
    defaultValues: toValues(DEFAULT_SETTINGS),
    values,
    resetOptions: { keepDirtyValues: true },
  });

  const onSubmit = async (v: FormOutput) => {
    const saved = await update.mutateAsync(v);
    // `reset()` inherits `resetOptions.keepDirtyValues`; after a save nothing must stay dirty.
    form.reset(toValues(saved), { keepDirtyValues: false });
  };

  return (
    <SettingsSection
      title="General"
      description="Commercial defaults and the rules of thumb every position and submission starts from."
    >
      {isAdmin ? null : <AdminNote />}
      {settings.isLoading ? (
        <SkeletonLines lines={6} />
      ) : settings.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Settings could not be loaded: {settings.error.message}
        </p>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5" noValidate>
            <fieldset disabled={!isAdmin} className="space-y-5">
              <SettingsCard
                title="Margin"
                description="The only commercial variable. Supplier ceilings and offers are derived from it."
              >
                <FormField
                  control={form.control}
                  name="margin_pct"
                  render={({ field }) => (
                    <FormItem className="max-w-xs">
                      <FormLabel>Global margin (%)</FormLabel>
                      <FormControl>
                        <Input type="number" inputMode="decimal" step="0.01" min={0} max={99.99} {...field} />
                      </FormControl>
                      <FormDescription>
                        Applied to every position unless a position or a submission overrides it.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </SettingsCard>

              <SettingsCard title="Rates and deadlines">
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="hours_per_day"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Hours per day</FormLabel>
                        <FormControl>
                          <Input type="number" inputMode="decimal" step="0.5" min={1} max={24} {...field} />
                        </FormControl>
                        <FormDescription>
                          Converts day rates to EUR/h (480 EUR/day = 60 EUR/h at 8).
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="feedback_due_working_days"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Feedback due (working days)</FormLabel>
                        <FormControl>
                          <Input type="number" inputMode="numeric" step="1" min={0} {...field} />
                        </FormControl>
                        <FormDescription>
                          After a submission is sent, feedback is expected within this many working days.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="urgent_start_days"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Urgent start (days)</FormLabel>
                        <FormControl>
                          <Input type="number" inputMode="numeric" step="1" min={0} {...field} />
                        </FormControl>
                        <FormDescription>
                          Positions starting within this window are flagged as urgent.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="consent_retention_months"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Consent retention (months)</FormLabel>
                        <FormControl>
                          <Input type="number" inputMode="numeric" step="1" min={1} {...field} />
                        </FormControl>
                        <FormDescription>
                          GDPR: how long a candidate's consent is valid from the consent date.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </SettingsCard>

              <SettingsCard
                title="Forbidden terms"
                description="Never appear in generated documents or messages. Every intermediary name is added automatically."
              >
                <FormField
                  control={form.control}
                  name="forbidden_terms"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Terms</FormLabel>
                      <FormControl>
                        <TagEditor
                          value={field.value}
                          onChange={field.onChange}
                          disabled={!isAdmin}
                          label="Forbidden terms"
                          placeholder="e.g. TechProjectsNow"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </SettingsCard>

              <SettingsCard title="Formats" description="Fixed in this version.">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="settings-date-format">Date format</Label>
                    <Input
                      id="settings-date-format"
                      value={settings.data?.date_format ?? ''}
                      readOnly
                      disabled
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="settings-time-zone">Time zone</Label>
                    <Input id="settings-time-zone" value={settings.data?.time_zone ?? ''} readOnly disabled />
                  </div>
                </div>
              </SettingsCard>
            </fieldset>

            {isAdmin ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button type="submit" loading={update.isPending} disabled={!form.formState.isDirty}>
                  Save changes
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => form.reset()}
                  disabled={!form.formState.isDirty}
                >
                  Discard
                </Button>
              </div>
            ) : null}
          </form>
        </Form>
      )}
    </SettingsSection>
  );
}
